//@author: mandy

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
    computed,
    inject,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { PreferenceService } from "../../../services/preference.service";
import { ContactService } from "../../../services/contact.service";
import { LoginStore } from "../../../stores/login.store";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";

@TypeManagerDecorator("system-email-defaults")
@Component({
    selector: "system-email-defaults",
    template: `
        <h2 class="c-margin-bottom--single">Email Defaults</h2>
        <div class="c-margin-bottom--single">
            <label for="email" class="ngBold c-margin-bottom--quarter"> Contact Admin Email Address </label>
            <p class="ngFinePrint c-margin-bottom--half">
                Required. Email address used for the Contact Admin link in the About section of the More menu.
            </p>
            <input
                type="text"
                id="email"
                [(ngModel)]="this.email"
                class="c-input"
                (ngModelChange)="this.validEmail()"
            />
            @if (!this.isValidEmail) {
                <p class="ngRed">Please enter a valid email address.</p>
            }
        </div>

        <div class="c-margin-bottom--single">
            <label for="show" class="ngBold c-margin-bottom--quarter"> Forgot Password Link </label>
            <p class="ngFinePrint c-margin-bottom-half">
                Setting that determines whether the forgot password link appears on the 25Live sign-in page. When shown,
                users can request a password reset email for their account.
            </p>
            <s25-toggle-button
                (valueChange)="this.onChange($event)"
                [value]="this.show"
                [trueLabel]="trueLabel"
                [falseLabel]="falseLabel"
            ></s25-toggle-button>
        </div>

        <div class="c-margin-bottom--single">
            <div class="ngBold c-margin-bottom--quarter">Copy Myself on All Emails</div>
            <p class="ngFinePrint c-margin-bottom--half">
                Default value for "Copy myself on all emails I send". Users can override this value in User Settings.
            </p>
            <s25-toggle-button
                [(value)]="this.emailCopyMe"
                [trueLabel]="'On'"
                [falseLabel]="'Off'"
                [disabled]="!canEdit()"
            ></s25-toggle-button>
        </div>

        <div class="c-margin-bottom--single">
            <label for="subDomain" class="ngBold c-margin-bottom--quarter"> Email Subdomain</label>
            <p class="ngFinePrint c-margin-bottom--half">
                Add a subdomain to emails sent from 25live for all users. Entering a subdomain here will also set a
                property for 'reply to'.
            </p>
            <input
                type="text"
                id="subDomain"
                [(ngModel)]="this.subDomain"
                class="c-input"
                [disabled]="!canEdit()"
                (ngModelChange)="sampleEmailFrom()"
            />
            <p>
                Example from email: <b>{{ this.sampleWithSubDomain }}</b>
            </p>
        </div>

        <div class="c-margin-bottom-single">
            <label for="globalEmail" class="ngBold c-margin-bottom--quarter">Global From Email Address</label>
            <p class="ngFinePrint c-margin-bottom--half">
                All email from 25live will be sent from this email address and reply to the user's configured work
                email.
            </p>
            <input
                type="text"
                id="globalEmail"
                [(ngModel)]="globalFrom"
                class="c-input"
                [disabled]="!canEdit()"
                (ngModelChange)="validGlobalEmail(); sampleEmailFrom()"
            />
            @if (this.globalMsg) {
                <p class="ngRed">{{ globalMsg }}</p>
            }
        </div>

        <div class="c-displayBlock c-padding-bottom--double">
            <button
                class="aw-button aw-button--primary c-margin-top--single c-margin-right--quarter"
                (click)="save()"
                [disabled]="!this.isValidEmail || this.globalMsg"
            >
                {{ this.loading ? "Saving...." : "Save" }}
            </button>
            <button class="aw-button aw-button--outline c-margin-top--single" (click)="cancel()">Cancel</button>
        </div>

        @if (message()) {
            <div class="ngGreen ngBold cn-alert cn-alert--success c-margin-bottom--single" role="alert">
                <div class="cn-alert__label">
                    <span>{{ message() }}</span>
                </div>
            </div>
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ToggleButtonModule, FormsModule],
})
export class EmailDefaultsComponent implements OnInit {
    @Input() falseLabel: string = "Hide forgot password link";
    @Input() trueLabel: string = "Show forgot password link";
    @Output() modelValueChange = new EventEmitter<boolean>();
    email: string = "";
    show: boolean = false;
    showRemind: string = "";
    subDomain: string = "";
    // replyTo: boolean = false;

    loading: boolean = false;
    isValidEmail: boolean = true;
    initEmail: string = "";
    initShowRemind: string = "";
    initSubDomain: string = "";
    initReplyTo: boolean = false;

    initGlobalFrom: string = "";
    globalFrom: string = "";
    globalMsg: string = "";

    emailCopyMe: boolean = false;
    initEmailCopyMe: string = "";

    userEmail: string = "";
    sampleWithSubDomain: string = "";

    canEdit = signal(false);
    message = signal<string>("");

    readonly loginStore = inject(LoginStore);

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private cd: ChangeDetectorRef,
    ) {}

    onChange($event: boolean) {
        this.modelValueChange.emit(this.show);
        this.show = $event;
        this.cd.detectChanges();
    }

    ngOnInit() {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
        this.getPrefs();
        this.cd.detectChanges();
    }

    getPrefs() {
        return S25Util.all({
            prefs: PreferenceService.getPreferences(
                [
                    "config_feedback_email",
                    "config_email_password",
                    "EmailSubdomain",
                    "EmailReplyTo",
                    "EmailFromAddress",
                    "EmailCopyMe",
                ],
                "S",
            ),
            userEmail: ContactService.getCurrentEmail(),
        }).then((resp) => {
            this.email = resp.prefs.config_feedback_email.value;
            resp.prefs.config_email_password.value === "show" ? (this.show = true) : (this.show = false);
            this.initEmail = resp.prefs.config_feedback_email.value;
            this.initShowRemind = resp.prefs.config_email_password.value;
            this.initSubDomain = resp.prefs.EmailSubdomain?.value ?? "";
            this.subDomain = resp.prefs.EmailSubdomain?.value ?? "";
            this.initReplyTo = !!resp.prefs.EmailReplyTo?.value;

            this.initGlobalFrom = resp.prefs.EmailFromAddress?.value ?? "";
            this.globalFrom = resp.prefs.EmailFromAddress?.value ?? "";

            this.initEmailCopyMe = resp.prefs.EmailCopyMe?.value ?? "F";
            this.emailCopyMe = resp.prefs.EmailCopyMe?.value === "T";

            this.userEmail = resp.userEmail;
            this.canEdit.set(this.loginStore.user()?.securityGroupId === -1);

            this.validEmail();
            this.sampleEmailFrom();
            this.cd.detectChanges();
        });
    }

    validEmail() {
        this.isValidEmail = S25Util.isValidEmail(this.email);
        this.cd.detectChanges();
    }

    validGlobalEmail() {
        this.globalMsg = "";
        if (this.globalFrom.includes("collegenet.com")) {
            this.globalMsg = "Global email cannot contain 'collegenet.com'";
        } else if (this.globalFrom && !S25Util.isValidEmail(this.globalFrom)) {
            this.globalMsg = "Please enter a valid email address.";
        }
        this.cd.detectChanges();
    }

    sampleEmailFrom() {
        this.sampleWithSubDomain = this.globalFrom
            ? this.applySubDomain(this.globalFrom)
            : this.applySubDomain(this.userEmail);
        this.cd.detectChanges();
    }

    /*
    Applies subdomain to the provided email address
     */
    applySubDomain(email: string) {
        return this.subDomain ? email.replace("@", "@" + this.subDomain + ".") : email;
    }

    save(): Promise<void> {
        this.show ? (this.showRemind = "show") : (this.showRemind = "hide");

        const promiseArr: Promise<unknown>[] = [];
        this.initEmail !== this.email &&
            promiseArr.push(PreferenceService.setPreference("config_feedback_email", this.email, "S"));
        this.initShowRemind !== this.showRemind &&
            promiseArr.push(PreferenceService.setPreference("config_email_password", this.showRemind, "S"));
        if (this.initSubDomain !== this.subDomain) {
            promiseArr.push(PreferenceService.setPreference("EmailSubdomain", this.subDomain, "S"));
            promiseArr.push(PreferenceService.setPreference("EmailReplyTo", this.subDomain ? 1 : 0, "S"));
        }
        this.initGlobalFrom !== this.globalFrom &&
            promiseArr.push(
                PreferenceService.setPreference("EmailFromAddress", this.globalFrom, "S").then(() => {
                    this.initGlobalFrom = this.globalFrom;
                }),
            );
        const emailCopyMeValue = this.emailCopyMe ? "T" : "F";
        this.initEmailCopyMe !== emailCopyMeValue &&
            promiseArr.push(PreferenceService.setPreference("EmailCopyMe", emailCopyMeValue, "S"));

        if (promiseArr.length) {
            this.loading = true;
            this.cd.detectChanges();
            return S25Util.all(promiseArr).then(() => {
                this.loading = false;
                this.message.set("Success!");
                this.cd.detectChanges();
            });
        }
        return Promise.resolve();
    }

    cancel() {
        this.getPrefs();
    }
}
